import React from 'react';
import './calendar.css';

/* ============================================================================
 * PlannerCalendar — the one calendar every RestoBOXX screen draws.
 *
 * Four screens used to draw their own month grid, and each had invented its
 * own vocabulary: a dot here, a ring there, weekends tinted on one and not on
 * the next. A manager reads all four in the same afternoon, so they now share
 * one grammar:
 *
 *   blue tint      the day is ON — the buffet runs, the branch opens
 *   rose ring      something is BOOKED on it, with the count in the corner
 *   violet wash    a public holiday: the island is out
 *   violet ring    a festival or observance: people are on the move
 *   cream band     Saturday and Sunday, behind everything else
 *   underline      today
 *
 * They stack, because a day is all of those at once — Divali is a holiday, a
 * Sunday, open, and heavily booked, and a calendar that can only say one of
 * those is why the old ones were re-invented per screen.
 *
 * The pieces are separate on purpose: a screen takes the chrome it needs and
 * nothing else. The buffet studio wants counts, quick actions and the holiday
 * list; Settings → Calendar wants the grid and the list; the buffet month view
 * wants one month and no chrome at all.
 * ========================================================================= */

/** Local-time ISO — never toISOString(), which shifts a date across the line. */
export const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1)
  .padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Monday-first offset for the 1st of a month (0 = Monday … 6 = Sunday). */
export const mondayLead = (d) => (new Date(d.getFullYear(), d.getMonth(), 1).getDay() + 6) % 7;

export const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

/** Every 1st-of-month between two dates, inclusive — what `months` wants. */
export function monthsBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return [];
  const a = new Date(`${fromIso}T00:00:00`);
  const b = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return [];
  const out = [];
  const cur = new Date(a.getFullYear(), a.getMonth(), 1);
  const stop = new Date(b.getFullYear(), b.getMonth(), 1);
  // A guard, not a limit anybody should hit: a bad pair of dates must not spin.
  while (cur <= stop && out.length < 60) {
    out.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

/* The weekday initials, in the viewer's own language. Built from a known
   Monday so the order is Monday-first whatever the locale's own week start. */
const DOW = (locale) => Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i)
  .toLocaleDateString(locale || undefined, { weekday: 'narrow' }));

/* The calendar's own tokens live on `.rbxcal`, so anything using these classes
   has to sit inside one. CalendarRoot is that and nothing else — for a grid
   dropped into a card the page already draws; CalendarPanel adds the framed
   panel for a calendar that IS the screen. */
export function CalendarRoot({ className = '', children }) {
  return <div className={`rbxcal ${className}`}>{children}</div>;
}

/** The panel the whole thing sits in. Every section is optional. */
export function CalendarPanel({ className = '', children }) {
  return (
    <CalendarRoot className={className}>
      <div className="rbxcal-panel">{children}</div>
    </CalendarRoot>
  );
}

export function CalendarHead({ title, subtitle, counts = [] }) {
  return (
    <div className="rbxcal-head">
      <div>
        {title ? <h3 className="rbxcal-title">{title}</h3> : null}
        {subtitle ? <p className="rbxcal-sub">{subtitle}</p> : null}
      </div>
      {counts.length > 0 && (
        <div className="rbxcal-counts">
          {counts.map((c) => (
            <div key={c.label} className={`rbxcal-count ${c.tone ? `is-${c.tone}` : ''}`}>
              <span className="rbxcal-count-n">{c.value}</span>
              <span className="rbxcal-count-l">{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CalendarToolbar({ children, legend }) {
  return (
    <div className="rbxcal-toolbar">
      <div className="rbxcal-actions">{children}</div>
      {legend ? <CalendarLegend items={legend} /> : <span />}
    </div>
  );
}

/** `items`: [{ key: 'open'|'weekend'|'booked'|'holiday'|'festival'|'today', label }] */
export function CalendarLegend({ items = [] }) {
  return (
    <div className="rbxcal-legend">
      {items.map((it) => (
        <span key={it.key} className="rbxcal-key">
          <i className={`rbxcal-sw k-${it.key}`} aria-hidden="true" />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/** One row of a day's hover bubble: a named thing, or a plain status line. */
function TipRow({ row }) {
  if (typeof row === 'string') {
    return <span className="rbxcal-tip-s">{row}</span>;
  }
  return (
    <span className="rbxcal-tip-r">
      {row.icon ? <span aria-hidden="true">{row.icon}</span> : null}
      <span className="rbxcal-tip-n">{row.name}</span>
      {row.tag ? <span className="rbxcal-tip-t">{row.tag}</span> : null}
    </span>
  );
}

/**
 * One month.
 *
 * `dayInfo(iso, date)` returns what is true of that day — everything is
 * optional, and a screen only answers for the facts it actually knows:
 *
 *   { on, booked, badge, holiday, festival, selected, disabled, dot, live,
 *     title, label, tip }
 *
 * `tip` is a list, and it is what a coloured rule under a day cannot say: the
 * NAME of the thing that is on. Each entry is either a plain string (a status
 * line — "Already open") or `{ icon, name, tag }` (the holiday itself). Give
 * it only for the days worth naming: it draws a bubble on hover and focus, and
 * the browser's own `title` tooltip is suppressed on those days so the two do
 * not both appear a second apart saying the same thing.
 *
 * `onDayClick(iso, date, event)` makes the cells buttons; without it they are
 * plain cells that cannot be pressed (a read-only planner should not look
 * clickable).
 */
export function CalendarMonth({
  month, dayInfo, onDayClick, todayIso, locale, className = '', hideHeader = false,
}) {
  const y = month.getFullYear();
  const m = month.getMonth();
  const lead = mondayLead(month);
  const count = new Date(y, m + 1, 0).getDate();
  const dow = DOW(locale);

  /* How far down the weekend band runs. A month whose last row stops before
     Saturday — September, starting on a Tuesday and ending on a Wednesday —
     otherwise leaves a grey block hanging under the grid with no days in it.
     The band is a fact about the columns, so it ends with the last row that
     actually has a weekend in it. */
  const rows = Math.ceil((lead + count) / 7);
  const lastRowFilled = (lead + count) % 7;
  const bandRows = (lastRowFilled === 0 || lastRowFilled > 5) ? rows : rows - 1;

  const cells = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: count }, (_, i) => new Date(y, m, i + 1)),
  ];

  return (
    <section className={`rbxcal-month ${className}`}>
      {/* Skipped where the screen around it already names the month — two
          "September 2026"s in one card is one too many. */}
      {hideHeader ? null : (
        <h4 className="rbxcal-month-h">
          {month.toLocaleDateString(locale || undefined, { month: 'long' })}
          <span>{y}</span>
        </h4>
      )}
      <div className="rbxcal-grid-wrap" style={{ '--cal-band-rows': bandRows }}>
        <div className="rbxcal-dow" aria-hidden="true">
          {dow.map((d, i) => <span key={i} className={i > 4 ? 'we' : undefined}>{d}</span>)}
        </div>
        <div className="rbxcal-grid">
          {cells.map((d, i) => {
            if (!d) return <div key={`pad${i}`} className="rbxcal-day pad" />;
            const iso = isoOf(d);
            const info = (dayInfo ? dayInfo(iso, d) : null) || {};
            const cls = [
              'rbxcal-day',
              isWeekend(d) ? 'we' : '',
              info.on ? 'on' : '',
              info.booked ? 'booked' : '',
              info.holiday ? 'hol' : '',
              info.festival ? 'fest' : '',
              info.selected ? 'sel' : '',
              /* Inside the planned range but deliberately NOT running — a day
                 struck out by hand, or one whose weekday is untimed. Distinct
                 from `out`, which is a day outside the range entirely: this
                 one is still clickable, because taking it back out is the
                 whole point of being able to see it. */
              info.skipped ? 'skip' : '',
              iso === todayIso ? 'today' : '',
              info.disabled ? 'out' : '',
              onDayClick && !info.disabled ? '' : 'flat',
            ].filter(Boolean).join(' ');

            const tip = Array.isArray(info.tip) && info.tip.length ? info.tip : null;
            const body = (
              <>
                <span className="rbxcal-num">{d.getDate()}</span>
                {info.badge != null && info.badge !== '' && (
                  <span className="rbxcal-badge">{info.badge}</span>
                )}
                {info.dot && <span className={`rbxcal-dot ${info.live ? 'is-live' : ''}`} />}
                {tip && (
                  <span className="rbxcal-tip" role="tooltip">
                    {tip.map((row, k) => <TipRow key={k} row={row} />)}
                  </span>
                )}
              </>
            );

            /* Which of the seven columns this cell sits in. The bubble is
               centred over the day, which would hang off the month at the
               edges — so the first two columns anchor it left and the last two
               anchor it right, and it stays inside the grid whatever the
               month's width. */
            const col = i % 7;
            /* Row 0's bubble would point up into the month heading, and the
               panel clips at its own edge — so the top row drops its bubble
               below the day instead. */
            const row = Math.floor(i / 7);
            // Suppressed where the bubble already says it — see `tip` above.
            const nativeTitle = tip ? undefined : (info.title || undefined);

            if (!onDayClick || info.disabled) {
              return (
                <div key={iso} className={cls} data-col={col} data-row={row}
                  title={nativeTitle} aria-label={info.label || undefined}>
                  {body}
                </div>
              );
            }
            return (
              <button
                key={iso}
                type="button"
                className={cls}
                data-col={col}
                data-row={row}
                title={nativeTitle}
                aria-label={info.label || undefined}
                aria-pressed={info.on ? true : undefined}
                aria-current={info.selected ? 'date' : undefined}
                onClick={(e) => onDayClick(iso, d, e)}
              >
                {body}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** A run of months as one wall planner. */
export function CalendarMonths({ months, single = false, ...rest }) {
  return (
    <div className={`rbxcal-months ${single || months.length === 1 ? 'is-single' : ''}`}>
      {months.map((m) => (
        <CalendarMonth key={`${m.getFullYear()}-${m.getMonth()}`} month={m} {...rest} />
      ))}
    </div>
  );
}

/** The named list under the grid: a coloured mark says "something is on", this says what. */
export function CalendarEvents({ title, items = [], note, noteWarn = false, children }) {
  return (
    <div className="rbxcal-foot">
      {title ? <h4 className="rbxcal-foot-h">{title}</h4> : null}
      {items.length > 0 && (
        <div className="rbxcal-events">
          {items.map((it) => (
            <div key={`${it.date}-${it.name}`} className={`rbxcal-event ${it.soft ? 'is-soft' : ''}`}>
              <span className="rbxcal-event-d">{it.day}</span>
              <span className="rbxcal-event-n" title={it.name}>
                {it.icon ? `${it.icon} ` : ''}{it.name}
              </span>
              {it.tag ? <span className="rbxcal-event-tag">{it.tag}</span> : null}
            </div>
          ))}
        </div>
      )}
      {children}
      {note ? <p className={`rbxcal-note ${noteWarn ? 'is-warn' : ''}`}>{note}</p> : null}
    </div>
  );
}

export function CalendarEmpty({ icon, children }) {
  return (
    <div className="rbxcal-empty">
      {icon}
      <p>{children}</p>
    </div>
  );
}

export default CalendarPanel;
